import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import Modal from '../../components/common/Modal';

const FeeManagement = () => {
    const navigate = useNavigate();
    const { get, post, put, del } = useApi();
    const { showToast } = useAppState();
    const [feeStructures, setFeeStructures] = useState([]);
    const [gradeLevels, setGradeLevels] = useState([]);
    const [academicTerms, setAcademicTerms] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editingFee, setEditingFee] = useState(null);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [feeToDelete, setFeeToDelete] = useState(null);
    const [formData, setFormData] = useState({
        grade_level: '',
        academic_term: '',
        tuition_amount: '',
        transport_amount: '',
        lunch_amount: '',
        parenting_amount: '',
        sports_amount: '',
        other_amount: '',
        is_active: true
    });

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [feesData, gradesData, termsData] = await Promise.all([
                get('/api/finance/fee-structures/'),
                get('/api/cbc/grade-levels/'),
                get('/api/academic-terms/')
            ]);
            setFeeStructures(feesData || []);
            setGradeLevels(gradesData || []);
            setAcademicTerms(termsData || []);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, [get]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingFee) {
                await put(`/api/finance/fee-structures/${editingFee.id}/`, formData);
            } else {
                await post('/api/finance/fee-structures/', formData);
            }
            fetchData();
            resetForm();
            showToast(`Fee structure ${editingFee ? 'updated' : 'created'} successfully`, 'success');
        } catch (error) {
            console.error('Error saving fee structure:', error);
            showToast('Failed to save fee structure', 'error');
        }
    };

    const handleEdit = (fee) => {
        setEditingFee(fee);
        setFormData({
            grade_level: fee.grade_level,
            academic_term: fee.academic_term,
            tuition_amount: fee.tuition_amount,
            transport_amount: fee.transport_amount,
            lunch_amount: fee.lunch_amount,
            parenting_amount: fee.parenting_amount,
            sports_amount: fee.sports_amount,
            other_amount: fee.other_amount,
            is_active: fee.is_active
        });
        setShowForm(true);
    };

    const handleDelete = (id) => {
        setFeeToDelete(id);
        setDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!feeToDelete) return;
        try {
            await del(`/api/finance/fee-structures/${feeToDelete}/`);
            fetchData();
            showToast('Framework successfully evicted from system registry', 'success');
        } catch (error) {
            console.error('Error deleting fee structure:', error);
            showToast('Failed to evict framework', 'error');
        } finally {
            setDeleteModalOpen(false);
            setFeeToDelete(null);
        }
    };

    const handleSyncStandardFees = async () => {
        setSyncing(true);
        try {
            await post('/api/finance/sync/standard-fees/');
            showToast('Frameworks synced successfully with BC', 'success');
            fetchData();
        } catch (error) {
            console.error('Error syncing:', error);
            showToast('Failed to sync with BC', 'error');
        } finally {
            setSyncing(false);
        }
    };

    const resetForm = () => {
        setFormData({
            grade_level: '',
            academic_term: '',
            tuition_amount: '',
            transport_amount: '',
            lunch_amount: '',
            parenting_amount: '',
            sports_amount: '',
            other_amount: '',
            is_active: true
        });
        setEditingFee(null);
        setShowForm(false);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const handleKeyDown = (e, nextFieldId) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const nextInput = document.getElementById(nextFieldId);
            if (nextInput) {
                nextInput.focus();
            }
        }
    };

    return (
        <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-10">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="flex items-center gap-5">
                    <div className="w-16 h-16 bg-[#18216D] rounded-[2rem] flex items-center justify-center shadow-2xl shadow-indigo-900/20">
                        <i className="fas fa-file-invoice-dollar text-white text-2xl"></i>
                    </div>
                    <div>
                        <h1 className="text-3xl font-black text-[#18216D] uppercase tracking-tighter">Fee Structures</h1>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mt-1.5 flex items-center gap-2">
                            Institutional Revenue Control
                        </p>
                    </div>
                </div>
                <div className="flex gap-4">
                    {!showForm && (
                        <>
                            <button
                                onClick={handleSyncStandardFees}
                                disabled={syncing}
                                className="px-6 py-3 bg-[#18216D] text-white rounded-2xl shadow-xl shadow-indigo-900/20 font-black text-[10px] uppercase tracking-widest transform hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50"
                            >
                                {syncing ? 'Syncing...' : 'Sync From BC'}
                            </button>
                            <button
                                onClick={() => setShowForm(true)}
                                className="px-8 py-4 bg-[#FFC425] text-[#18216D] rounded-2xl shadow-xl shadow-yellow-500/20 font-black text-[10px] uppercase tracking-[0.2em] transform hover:scale-[1.02] active:scale-[0.98] transition-all"
                            >
                                + New Fee Framework
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Form */}
            {showForm && (
                <div className="bg-white rounded-[3rem] shadow-2xl shadow-indigo-900/5 border border-indigo-50/50 p-10 relative overflow-hidden animate-in slide-in-from-top-4 duration-500">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-yellow-50/30 rounded-full -mr-32 -mt-32 blur-3xl"></div>

                    <div className="flex items-center justify-between mb-10 relative z-10">
                        <h2 className="text-xl font-black text-[#18216D] uppercase tracking-tight">
                            {editingFee ? 'Refine Fee Structure' : 'New Revenue Framework'}
                        </h2>
                        <button onClick={resetForm} className="text-slate-400 hover:text-rose-600 transition-colors">
                            <i className="fas fa-times"></i>
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-8 relative z-10">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Grade Level</label>
                                <select
                                    value={formData.grade_level}
                                    onChange={(e) => setFormData({ ...formData, grade_level: e.target.value })}
                                    required
                                    className="w-full px-5 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-[#18216D] font-bold text-sm transition-all"
                                >
                                    <option value="">Select Target Grade</option>
                                    {gradeLevels.map(grade => (
                                        <option key={grade.id} value={grade.id}>{grade.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="md:col-span-2 space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Academic Term</label>
                                <select
                                    value={formData.academic_term}
                                    onChange={(e) => setFormData({ ...formData, academic_term: e.target.value })}
                                    required
                                    className="w-full px-5 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-[#18216D] font-bold text-sm transition-all"
                                >
                                    <option value="">Select Target Term (Academic Year)</option>
                                    {academicTerms.map(term => (
                                        <option key={term.id} value={term.id}>
                                            {term.name} — {academicTerms.find(t => t.id === term.id)?.year_name || ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="h-px bg-slate-100"></div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {[
                                { id: 'tuition_amount', label: 'Tuition Fee', next: 'transport_amount' },
                                { id: 'transport_amount', label: 'Uniform Fee', next: 'lunch_amount' },
                                { id: 'lunch_amount', label: 'Lunch', next: 'parenting_amount' },
                                { id: 'parenting_amount', label: 'Parenting Seminar/Course', next: 'sports_amount' },
                                { id: 'sports_amount', label: 'Student Insurance', next: 'other_amount' },
                                { id: 'other_amount', label: 'Portal Access', next: 'submit_btn' }
                            ].map((field) => (
                                <div key={field.id} className="space-y-2 group">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block group-focus-within:text-[#18216D] transition-colors">{field.label}</label>
                                    <div className="relative">
                                        <span className="absolute left-5 top-1/2 -translate-y-1/2 text-[10px] font-black text-slate-300">KES</span>
                                        <input
                                            id={field.id}
                                            type="number"
                                            value={formData[field.id]}
                                            onChange={(e) => setFormData({ ...formData, [field.id]: e.target.value })}
                                            onKeyDown={(e) => handleKeyDown(e, field.next)}
                                            required={field.id === 'tuition_amount'}
                                            min="0"
                                            step="0.01"
                                            className="w-full pl-14 pr-5 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-[#FFC425] font-black text-sm transition-all"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="flex items-center gap-4 bg-slate-50 p-4 rounded-2xl w-fit">
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#18216D]"></div>
                                <span className="ml-3 text-[10px] font-black text-gray-700 uppercase tracking-widest">Active Framework</span>
                            </label>
                        </div>

                        <div className="flex gap-4 pt-4">
                            <button
                                type="submit"
                                id="submit_btn"
                                className="px-12 py-4 bg-[#18216D] text-white rounded-2xl shadow-xl shadow-indigo-900/20 font-black text-[10px] uppercase tracking-[0.2em] hover:scale-[1.02] transition-all"
                            >
                                {editingFee ? 'Save Framework' : 'Publish Framework'}
                            </button>
                            <button
                                type="button"
                                onClick={resetForm}
                                className="px-12 py-4 bg-slate-100 text-slate-400 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] hover:bg-slate-200 transition-all"
                            >
                                Discard
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Fee Structures List */}
            <div className="bg-white rounded-[3rem] shadow-xl shadow-indigo-900/5 border border-indigo-50/50 overflow-hidden">
                <div className="p-10">
                    <div className="flex items-center justify-between mb-8">
                        <h2 className="text-xl font-black text-[#18216D] uppercase tracking-tight">Active Frameworks</h2>
                        <div className="flex items-center gap-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                            <span className="flex items-center gap-2"><i className="fas fa-circle text-emerald-500 text-[6px]"></i> Operational</span>
                            <span className="flex items-center gap-2"><i className="fas fa-circle text-slate-200 text-[6px]"></i> Draft</span>
                        </div>
                    </div>

                    {loading ? (
                        <div className="py-20 text-center animate-pulse text-[10px] font-black text-slate-300 uppercase tracking-widest">Querying Ledger...</div>
                    ) : feeStructures.length === 0 ? (
                        <div className="py-20 text-center bg-slate-50/50 rounded-[2rem] border border-dashed border-slate-200">
                            <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">No fee framework defined</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto -mx-10 px-10">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-slate-50">
                                        <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Framework Context</th>
                                        <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Tuition</th>
                                        <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Extras</th>
                                        <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Liability</th>
                                        <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                                        <th className="px-6 py-5 text-right text-[10px] font-black text-slate-400 uppercase tracking-widest">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {feeStructures.map(fee => (
                                        <tr key={fee.id} className="hover:bg-slate-50/50 transition-colors group">
                                            <td className="px-6 py-6">
                                                <div className="font-black text-[#18216D] text-sm">{fee.grade_level_name}</div>
                                                <div className="text-[9px] font-bold text-slate-400 uppercase mt-1">{fee.academic_term_name} — {fee.academic_year_name}</div>
                                            </td>
                                            <td className="px-6 py-6 text-sm font-bold text-gray-700">{formatCurrency(fee.tuition_amount)}</td>
                                            <td className="px-6 py-6 font-medium text-slate-400">
                                                <div className="flex flex-wrap gap-2">
                                                    {fee.lunch_amount > 0 && <span className="text-[8px] bg-slate-100 px-1.5 py-0.5 rounded uppercase tracking-tighter">Lunch</span>}
                                                    {fee.transport_amount > 0 && <span className="text-[8px] bg-slate-100 px-1.5 py-0.5 rounded uppercase tracking-tighter">Uniform</span>}
                                                    {fee.sports_amount > 0 && <span className="text-[8px] bg-slate-100 px-1.5 py-0.5 rounded uppercase tracking-tighter">Insurance</span>}
                                                    {fee.other_amount > 0 && <span className="text-[8px] bg-slate-100 px-1.5 py-0.5 rounded uppercase tracking-tighter">Portal</span>}
                                                </div>
                                            </td>
                                            <td className="px-6 py-6">
                                                <div className="text-sm font-black text-[#18216D]">{formatCurrency(fee.total_amount)}</div>
                                            </td>
                                            <td className="px-6 py-6">
                                                <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${fee.is_active ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-slate-50 text-slate-400 border border-slate-100'}`}>
                                                    {fee.is_active ? 'Operational' : 'Draft'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-6 text-right">
                                                <div className="flex justify-end gap-2 opacity-100 lg:opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button onClick={() => handleEdit(fee)} className="w-9 h-9 flex items-center justify-center bg-white border border-indigo-50 text-[#18216D] rounded-xl hover:bg-[#18216D] hover:text-white transition-all shadow-sm">
                                                        <i className="fas fa-edit text-xs"></i>
                                                    </button>
                                                    <button onClick={() => handleDelete(fee.id)} className="w-9 h-9 flex items-center justify-center bg-white border border-rose-50 text-rose-500 rounded-xl hover:bg-rose-500 hover:text-white transition-all shadow-sm">
                                                        <i className="fas fa-trash text-xs"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
            {/* Deletion Confirmation Modal */}
            <Modal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                title="Eviction Warning"
                type="danger"
                footer={
                    <>
                        <button
                            onClick={confirmDelete}
                            className="w-full sm:w-auto px-10 py-4 bg-rose-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] shadow-xl shadow-rose-900/20 hover:scale-[1.02] active:scale-95 transition-all"
                        >
                            Confirm Eviction
                        </button>
                        <button
                            onClick={() => setDeleteModalOpen(false)}
                            className="w-full sm:w-auto px-10 py-4 bg-slate-100 text-slate-400 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] hover:bg-slate-200 transition-all font-mono"
                        >
                            Cancel
                        </button>
                    </>
                }
            >
                You are about to permanently remove this **Fee Framework** from the active ledger. This action cannot be undone and may affect billing processes.
            </Modal>
        </div>
    );
};

export default FeeManagement;
